import { Pointer } from "rfc6902";
import { getFromDataPath } from "../../../utility/getFromDataPath";
import { mutateByPath } from "../../../utility/mutateByPath";
import { AnalysisMethod } from "../../ars_ldm";
import { normalizeParameterName } from "../../utility/normalizeParameterName";
import { ArsTableBody } from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import { collectParameterPaths } from "./collectParameterPaths";

/**
 * Shares parameters between entries of the edited item's type within its table entry.
 * Operation and grouping-factor parameter values remain separate.
 * Operation target variables are shared with the same operation peers.
 * Separate top-level entries and the opposite table axis keep their own values.
 *
 * @param body - Table body containing the item being edited.
 * @param itemPath - JSON Pointer path to the edited operation or grouping factor.
 * @param inheritedMethod - Current table method resolved from ARS context in table state.
 * @param isT - Type guard for the generic table group type T.
 * @param inserting - Whether the item at itemPath was just inserted and needs values from a peer.
 * @returns An immutable body with matching parameter values across entries of the same type.
 */
export const syncParameters = <T>(
  body: ArsTableBody<T>,
  itemPath: string,
  inheritedMethod: AnalysisMethod,
  isT: (obj: unknown) => obj is T,
  inserting = false,
): ArsTableBody<T> => {
  const pathParts = itemPath.split("/").filter(Boolean);
  const section = pathParts[0];
  const ownerIndex = Number(pathParts[1]);
  if (
    (section !== "columns" && section !== "rows") ||
    !Number.isInteger(ownerIndex) ||
    ownerIndex < 0 ||
    ownerIndex >= body[section].length
  ) {
    return body;
  }

  const source = getFromDataPath(itemPath, body, (obj) => isArsTableGroupCalculationDto(obj, isT));
  if (!source || !("operationId" in source || "groupingFactorId" in source)) return body;

  const valueSource = "operationId" in source ? "operation" : "groupingFactor";
  const sourceKey = valueSource === "operation" ? "operationId" : "groupingFactorId";
  const tableGroups = body[section];
  const owner = tableGroups[ownerIndex];
  const targets = tableGroups.every((group) => sourceKey in group)
    ? tableGroups.flatMap((group, index) =>
        collectParameterPaths(group, `/${section}/${index}`, valueSource),
      )
    : collectParameterPaths(owner, `/${section}/${ownerIndex}`, valueSource);
  const parameters =
    inheritedMethod.codeTemplate?.parameters?.filter(
      (parameter) => parameter.valueSource === valueSource,
    ) ?? [];
  const peerItems = inserting
    ? targets
        .filter((targetPath) => targetPath !== itemPath)
        .map((targetPath) =>
          getFromDataPath(targetPath, body, (obj) => isArsTableGroupCalculationDto(obj, isT)),
        )
        .filter((target): target is NonNullable<typeof target> => target !== undefined)
    : [];
  const sourceForSync = inserting
    ? (peerItems.find(
        (peer) =>
          parameters.some(
            (parameter) => peer.parameters?.[normalizeParameterName(parameter.name)] !== undefined,
          ) ||
          ("operationId" in peer && peer.targetVariable !== ""),
      ) ?? peerItems[0])
    : source;
  if (!sourceForSync) return body;

  let result = body;
  for (const parameter of parameters) {
    for (const targetPath of targets) {
      const parameterPath = Pointer.fromJSON(`${targetPath}/parameters`);
      parameterPath.push(normalizeParameterName(parameter.name));
      result = mutateByPath(
        result,
        parameterPath.toString(),
        sourceForSync.parameters?.[normalizeParameterName(parameter.name)],
      );
    }
  }
  if ("operationId" in sourceForSync) {
    for (const targetPath of targets) {
      result = mutateByPath(result, `${targetPath}/targetVariable`, sourceForSync.targetVariable);
    }
  }
  return result;
};
